/**
 * **Bersihkan salinan tablet** — dengan PIN administrator (Imron, 2026-08-22:
 * *"apakah ada cara dari UI (pakai pin administrator) untuk clear cache tablet?"*).
 *
 * ### ⛔⛔ Yang TIDAK ikut dibuang, dan kenapa
 * - **Antrean `outbox`** — itu PENJUALAN yang belum diakui portal. Membuangnya menghapus uang yang
 *   sudah diterima dari tamu, permanen, tanpa jejak di mana pun. ⇒ selama antreannya berisi,
 *   tombol ini MENOLAK bekerja dan menyebut angkanya; "bersihkan cache" tak boleh jadi jalan
 *   pintas menghilangkan penjualan yang menyusahkan.
 * - **Token & pasangan perangkat** — membuangnya berarti tablet harus dipasangkan ULANG dengan
 *   kode dari portal, dan yang menemukannya adalah orang yang berdiri di depan antrean.
 *
 * ### ⛔ Kenapa PIN diminta ULANG, dan kenapa harus DARING
 * Yang dibuang termasuk katalog & aturan tanggal usaha — jaring luring tablet (K13). Melakukannya
 * saat server tak terjangkau berarti membuang satu-satunya salinan menu **tepat ketika ia tak bisa
 * diambil lagi**. ⇒ ditolak saat luring, bukan dicoba lalu gagal separuh jalan.
 *
 * ⚠️ PIN diverifikasi SERVER lewat jalur login biasa — bukan dicocokkan di layar. Dan izinnya
 * `master:edit`: yang boleh mengubah setelan, bukan siapa pun yang kebetulan memegang tablet.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CACHE_KEYS, cacheDrop } from '../api/cache';
import { api } from '../api/client';
import { pendingCount } from '../api/outbox';

export interface ClearCacheButtonProps {
  readonly outletCode: string;
  readonly deviceCode: string;
  readonly online: boolean;
}

export function ClearCacheButton(p: ClearCacheButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [staffCode, setStaffCode] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const antre = pendingCount();
  const boleh = antre === 0 && p.online && staffCode.trim() !== '' && pin.length >= 4 && !busy;

  const bersihkan = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const sesi = await api.loginPin({ staffCode: staffCode.trim().toUpperCase(), pin });
      /* ⛔ Izin diperiksa dari jawaban SERVER, bukan dari sesi yang sedang berjalan di layar. */
      if (!sesi.permissions.includes('master:edit')) {
        setError(t('cashier.clearCache.needsPermission'));
        return;
      }
      cacheDrop(CACHE_KEYS.whoAmI);
      cacheDrop(CACHE_KEYS.businessDateRule(p.outletCode));
      cacheDrop(CACHE_KEYS.shift(p.outletCode, p.deviceCode));
      /*
       * ⚠️ Katalog & verifier PIN luring disapu lewat AWALAN: keduanya berkunci majemuk
       * (kanal harga, kode staf) dan daftar yang diketik tangan akan meninggalkan sisa tiap kali
       * ada kanal atau staf baru — sisa yang justru paling membingungkan, karena layar terlihat
       * bersih sambil tetap memakai salinan lama.
       */
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('osbpos.cache.')) cacheDrop(k);
      }
      /* ⚠️ Sesi staf ikut: yang membersihkan tablet biasanya menyerahkannya ke orang lain. */
      localStorage.removeItem('osbpos.staf');
      location.reload();
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="action-secondary"
        data-testid="clear-cache"
        onClick={() => { setOpen(true); }}
      >
        {t('cashier.clearCache.button')}
      </button>

      {open && (
        <div className="dialog" role="dialog" data-testid="clear-cache-dialog">
          <h2>{t('cashier.clearCache.button')}</h2>
          <p className="dialog-note">{t('cashier.clearCache.what')}</p>
          {/* ⭐ Yang TIDAK dibuang disebut sejelas yang dibuang — itu yang menghentikan orang
              mencari cara lain yang lebih merusak. */}
          <p className="dialog-note">{t('cashier.clearCache.keeps')}</p>

          {antre > 0 && (
            <p className="error" data-testid="clear-cache-blocked">
              {t('cashier.clearCache.pending', { n: antre })}
            </p>
          )}
          {!p.online && (
            <p className="error" data-testid="clear-cache-offline">
              {t('cashier.clearCache.offline')}
            </p>
          )}

          <label>
            {t('cashier.clearCache.staffCode')}
            <input
              data-testid="clear-cache-staff"
              value={staffCode}
              onChange={(e) => setStaffCode(e.target.value)}
            />
          </label>
          <label>
            {t('cashier.clearCache.pin')}
            <input
              type="password"
              inputMode="numeric"
              data-testid="clear-cache-pin"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </label>

          {error !== null && <p className="error" data-testid="clear-cache-error">{error}</p>}

          <div className="dialog-actions">
            <button type="button" data-testid="clear-cache-batal" onClick={() => { setOpen(false); }}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="clear-cache-ok"
              disabled={!boleh}
              onClick={() => void bersihkan()}
            >
              {busy ? t('common.loading') : t('cashier.clearCache.confirm')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
